import { Button, Skeleton, Switch } from "@pushpress/pushpress-ui";
import { Shield, UserCog, Users } from "lucide-react";
import { toast } from "react-toastify";
import { queryClient } from "../../../../components/providers/ReactQueryProvider";
import SectionHeader from "../shared/SectionHeader";
import SectionTitle from "../shared/SectionTitle";
import { getUserType } from "../../helpers";
import { useUpdateBlockUser } from "../../../../apis/userApi.hooks";
import { defaultToastOptions } from "../../../../utils/misc";
import type { V2Member } from "../../../../apis/userApi";

type SettingsSectionProps = {
  userUuid: string;
  profileData: V2Member;
  isDataLoading: boolean;
};

export default function Settings({
  userUuid,
  profileData,
  isDataLoading,
}: SettingsSectionProps) {
  const { mutate: updateBlockedUser } = useUpdateBlockUser();
  const isBlocked = profileData && !!profileData.blockedAt;

  const handleMergeAccount = () => {
    window.location.href = `/members/merge_search/${userUuid}`;
  };

  const handleChangeRole = () => {
    window.location.href = `/users/edit/${userUuid}/promote`;
  };

  const handleDeleteAccount = () => {
    window.location.href = `/members/view/${userUuid}`;
  };

  const handleBlockAccount = () => {
    updateBlockedUser(
      { isBlocked, userUuid },
      {
        onSuccess: () => {
          toast.success(
            `User is ${isBlocked ? "unblocked" : "blocked"}`,
            defaultToastOptions,
          );
          /* Update status in tanstack cache for instant visual update.
           The data will be re-fetched when the user re-visits the page */
          queryClient.setQueryData(
            ["client-user-profile", userUuid],
            (cachedData: V2Member) => {
              return {
                ...cachedData,
                blockedAt: isBlocked ? null : new Date().toISOString(),
              };
            },
          );
        },
        onError: () => {
          toast.error(
            "There was an error updating the user",
            defaultToastOptions,
          );
        },
      },
    );
  };

  return (
    <div className="tw-space-y-4">
      <SectionHeader title="Settings" />
      <div className="tw-rounded-lg tw-border tw-p-4 tw-bg-white tw-flex tw-flex-row tw-gap-4">
        <div className="tw-flex tw-flex-col tw-gap-4 tw-w-1/2">
          <div className="tw-rounded-lg tw-border tw-p-3">
            <div className="tw-flex tw-items-start tw-justify-between">
              <SectionTitle icon={Shield} title="Account Status Blocked" />
              {isDataLoading ? (
                <Skeleton className="tw-h-8 tw-w-20" />
              ) : (
                <Switch
                  id="blocked-toggle"
                  className="tw-mt-1"
                  checked={isBlocked}
                  onCheckedChange={() => handleBlockAccount()}
                />
              )}
            </div>
            <p className="tw-text-sm tw-text-neutral-600 tw-ml-9">
              When blocked, user cannot post on the Member social feed in the
              Member App
            </p>
          </div>
          <div className="tw-rounded-lg tw-border tw-p-3">
            <div className="tw-flex tw-items-center tw-justify-between">
              <SectionTitle icon={UserCog} title="User Role" />
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleChangeRole()}
              >
                Change Role
              </Button>
            </div>
            <p className="tw-text-sm tw-text-neutral-600 tw-ml-9 tw-flex tw-items-center tw-gap-2 tw-w-[250px]">
              Current role:{" "}
              {isDataLoading ? (
                <Skeleton className="tw-h-4 tw-w-1/2" />
              ) : (
                getUserType(profileData && profileData.membershipRole)
              )}
            </p>
          </div>
        </div>
        <div className="tw-rounded-lg tw-border tw-p-3 tw-w-1/2">
          <div className="tw-mb-4">
            <SectionTitle icon={Users} title="Account Management" />
            <p className="tw-text-sm tw-text-neutral-600 tw-ml-9">
              Manage account merging and deletion
            </p>
          </div>
          <div>
            <div className="tw-flex tw-items-center tw-justify-between tw-pb-2">
              <div>
                <h4 className="tw-text-[16px]">Merge Account</h4>
                <p className="tw-text-sm tw-text-neutral-600 tw-pt-1">
                  Combine this account with another existing account
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleMergeAccount()}
              >
                Merge
              </Button>
            </div>
            <div className="tw-flex tw-items-center tw-justify-between tw-pt-2">
              <div>
                <h4 className="tw-text-[16px] tw-text-error">Delete Account</h4>
                <p className="tw-text-sm tw-text-neutral-600 tw-pt-1">
                  Permanently delete this account and all associated data
                </p>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleDeleteAccount()}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
